import React, { useState } from "react";
import { api, ApiError } from "../../api";
import { useAuth } from "../../AuthContext";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";
import { AadhaarPhotoFields } from "./AadhaarPhotoFields";

export function ProfileScreen({ onBack }: { onBack: () => void }) {
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();
  const [name, setName] = useState(user?.name ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [city, setCity] = useState(user?.city ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");

  const [idPhotoFront, setIdPhotoFront] = useState("");
  const [idPhotoBack, setIdPhotoBack] = useState("");
  const [vBusy, setVBusy] = useState(false);
  const [vError, setVError] = useState("");
  const verificationStatus = user?.verificationStatus ?? "unverified";

  async function save() {
    setBusy(true);
    setError("");
    try {
      const { user: updated } = await api.patch("/api/auth/me", { name, phone: phone || null, city });
      updateUser(updated);
      toast(t("settings.profileUpdated"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  async function submitVerification() {
    setVError("");
    if (!idPhotoFront || !idPhotoBack) return setVError(t("settings.errIdPhotosRequired"));
    setVBusy(true);
    try {
      const { user: updated } = await api.post("/api/auth/me/verification", {
        idPhotoFront,
        idPhotoBack,
      });
      updateUser(updated);
      setIdPhotoFront("");
      setIdPhotoBack("");
      toast(t("settings.verificationSubmitted"));
    } catch (err) {
      setVError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setVBusy(false);
    }
  }

  async function changePassword() {
    setPwError("");
    if (!currentPassword) return setPwError(t("settings.errCurrentPasswordRequired"));
    if (newPassword.length < 6) return setPwError(t("auth.errPasswordLength"));
    if (newPassword !== confirmPassword) return setPwError(t("auth.errPasswordMismatch"));
    setPwBusy(true);
    try {
      await api.patch("/api/auth/password", { currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast(t("settings.passwordChanged"));
    } catch (err) {
      setPwError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <button className="icon-btn" onClick={onBack} aria-label="Back">‹</button>
        <div className="section-title" style={{ marginBottom: 0 }}>{t("settings.profileMenu")}</div>
      </div>
      {error && <div className="error-banner">{error}</div>}
      <div className="card">
        <div className="sub">{t("settings.emailUsedToLogIn")}</div>
        <b>{user?.email}</b>
      </div>
      <div className="field">
        <label>{t("settings.name")}</label>
        <div className="input-wrap"><input value={name} onChange={(e) => setName(e.target.value)} /></div>
      </div>
      <div className="field">
        <label>{t("settings.phone")}</label>
        <div className="input-wrap">
          <input
            type="tel"
            maxLength={10}
            value={phone ?? ""}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder={t("settings.phonePlaceholder")}
          />
        </div>
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label>{t("settings.city")}</label>
        <div className="input-wrap"><input value={city} onChange={(e) => setCity(e.target.value)} /></div>
      </div>
      <button className="btn-solid btn-sm" style={{ marginTop: 14 }} disabled={busy} onClick={save}>
        {busy ? t("common.saving") : t("common.save")}
      </button>

      <div className="section-title" style={{ marginTop: 24 }}>{t("settings.verificationTitle")}</div>
      {verificationStatus === "verified" ? (
        <div className="card" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "var(--good)", fontSize: 18 }}>✓</span>
          <b>{t("settings.verificationVerified")}</b>
        </div>
      ) : verificationStatus === "pending" ? (
        <div className="card">
          <b>{t("settings.verificationPending")}</b>
          <div className="sub" style={{ marginTop: 4 }}>{t("settings.verificationPendingSub")}</div>
        </div>
      ) : (
        <>
          {verificationStatus === "rejected" && (
            <div className="error-banner">
              {t("settings.verificationRejected")}
              {user?.verificationNote ? `: ${user.verificationNote}` : ""}
            </div>
          )}
          <div className="sub" style={{ marginBottom: 10 }}>{t("settings.verificationHint")}</div>
          {vError && <div className="error-banner">{vError}</div>}
          <AadhaarPhotoFields front={idPhotoFront} back={idPhotoBack} onChangeFront={setIdPhotoFront} onChangeBack={setIdPhotoBack} />
          <button className="btn-solid btn-sm" style={{ marginTop: 6 }} disabled={vBusy} onClick={submitVerification}>
            {vBusy ? t("common.saving") : t("settings.verificationSubmit")}
          </button>
        </>
      )}

      <div className="section-title" style={{ marginTop: 24 }}>{t("settings.changePasswordTitle")}</div>
      {pwError && <div className="error-banner">{pwError}</div>}
      <div className="field">
        <label>{t("settings.currentPasswordLabel")}</label>
        <div className="input-wrap">
          <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label>{t("auth.newPasswordLabel")}</label>
        <div className="input-wrap">
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t("auth.newPasswordPlaceholder")}
          />
        </div>
      </div>
      <div className="field" style={{ marginBottom: 0 }}>
        <label>{t("auth.confirmPasswordLabel")}</label>
        <div className="input-wrap">
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
        </div>
      </div>
      <button className="btn-solid btn-sm" style={{ marginTop: 14 }} disabled={pwBusy} onClick={changePassword}>
        {pwBusy ? t("common.saving") : t("settings.changePasswordButton")}
      </button>
    </>
  );
}
